import React, { useEffect, useState, useRef, useCallback } from "react";
import "../styles/AdminAbout.css";
import { ABOUT_API, ABOUT_UPLOAD_API, API_BASE } from "../config/api";
import { showConfirm } from "../utils/customDialog";

const AdminAbout = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("narrative");

  const popupRef = useRef(null);

  // ================= POPUP =================
  const showPopup = (msg, type = "success") => {
    if (popupRef.current) popupRef.current.remove();

    const div = document.createElement("div");
    popupRef.current = div;

    div.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        right: 20px;
        background: ${type === "error" ? "#dc2626" : "#5d1d2b"};
        color: #fff;
        padding: 14px 18px;
        border-radius: 10px;
        z-index: 99999;
        box-shadow: 0 10px 25px rgba(93, 29, 43, 0.25);
        font-size: 14px;
        font-weight: 600;
        max-width: 300px;
        animation: slideIn 0.3s ease;
      ">
        ${msg}
      </div>
      <style>
        @keyframes slideIn {
          from { transform: translateY(-20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      </style>
    `;

    document.body.appendChild(div);

    setTimeout(() => {
      div.remove();
      popupRef.current = null;
    }, 2500);
  };

  const [vaithikasriList, setVaithikasriList] = useState([]);
  const [founderList, setFounderList] = useState([]);
  const [editorList, setEditorList] = useState([]);
  const [editingKey, setEditingKey] = useState(null);

  const handleEditEntry = (typeLabel, index, name = "") => {
    const key = `${typeLabel.toLowerCase().replace(/\s+/g, '_')}_${index}`;
    setEditingKey(key);
    showPopup(`Editing ${typeLabel}${name ? `: ${name}` : ` #${index + 1}`}`);

    setTimeout(() => {
      const cardEl = document.getElementById(`entry-card-${key}`);
      if (cardEl) {
        cardEl.scrollIntoView({ behavior: "smooth", block: "center" });
        const firstInput = cardEl.querySelector("input[type='text'], textarea");
        if (firstInput) {
          firstInput.focus();
        }
      }
    }, 100);
  };

  // ================= FETCH DATA =================
  const fetchAboutData = useCallback(async () => {
    try {
      const res = await fetch(ABOUT_API);
      const data = await res.json();

      if (data.status === "success") {
        const vaithikasriData = data.data.vaithikasri_section || [];

        vaithikasriData.forEach((v) => {
          const rawDesc = typeof v.description === "string"
            ? v.description
            : (Array.isArray(v.description) ? v.description.join("\n") : "");

          const lines = rawDesc.split("\n").map((l) => l.trim()).filter(Boolean);
          const leadLines = [];
          const features = [];
          const closingLines = [];
          let foundFeatures = false;

          lines.forEach((line) => {
            if (
              line.toLowerCase().includes("salient feature") ||
              line.toLowerCase().includes("features of our issue")
            ) {
              foundFeatures = true;
              return;
            }

            const colonIdx = line.indexOf(":");
            if (colonIdx !== -1 && (foundFeatures || colonIdx < 30)) {
              features.push({
                title: line.substring(0, colonIdx).trim(),
                detail: line.substring(colonIdx + 1).trim(),
              });
            } else if (foundFeatures && features.length > 0) {
              closingLines.push(line);
            } else {
              leadLines.push(line);
            }
          });

          v.intro_lead = leadLines.join("\n");
          v.features = features.length > 0 ? features : [
            { title: "Monthly almanac", detail: "highlighting daily Tithis" },
            { title: "Festivals", detail: "Details of festivals and Viratam" },
            { title: "Doubts Dispelled", detail: "Clarifications from scholars" },
            { title: "Wonderful Stotras", detail: "Effective stotras" },
            { title: "Ethical Sayings", detail: "Spiritual sayings" },
            { title: "Bala Sanskrit", detail: "Sanskrit lessons" },
            { title: "Spiritual News", detail: "Religious events" },
            { title: "Matrimony", detail: "Alliances for brides & grooms" },
            { title: "Ethical Science", detail: "Moral guidance" },
          ];
          v.closing_note = closingLines.join("\n") || "Several other interesting features faithfully adhering to Veda Sastras and religious literature.";
          v.image1_caption = v.image1_caption || "Sri Kanchi Kamakoti Sabha & Blessing";
          v.image2_caption = v.image2_caption || "Varshika Veda Poorti Pariksha";
        });

        setVaithikasriList(vaithikasriData);
        setFounderList(data.data.founder_section || []);
        setEditorList(data.data.editor_section || []);

        showPopup("Data loaded successfully");
      } else {
        showPopup(data.message || "Fetch error", "error");
      }
    } catch (err) {
      console.error("Failed:", err);
      showPopup("Failed to load data", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAboutData();
  }, [fetchAboutData]);

  // ================= IMAGE UPLOAD =================
  const handleImageUpload = async (file, callback) => {
    if (!file) return;

    const formData = new FormData();
    formData.append("image", file);

    try {
      const res = await fetch(ABOUT_UPLOAD_API, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();

      if (data.status === "success") {
        callback(data.path);
        showPopup("Image uploaded successfully");
      } else {
        showPopup("Image upload failed", "error");
      }
    } catch (err) {
      console.error(err);
      showPopup("Upload error", "error");
    }
  };

  // ================= ADD ENTRY =================
  const handleAddEntry = (setter, template) => {
    setter((prev) => [...prev, { ...template }]);
    showPopup("Entry added");
  };

  // ================= DELETE ENTRY =================
  const handleDeleteEntry = async (sectionName, setter, index, item, typeLabel = "Entry") => {
    const ok = await showConfirm({
      title: `Delete ${typeLabel}`,
      message: `Are you sure you want to permanently delete this ${typeLabel}?`,
      subMessage: "This will remove the item from the About page sections.",
      confirmText: `Delete ${typeLabel}`,
      type: "danger"
    });
    if (!ok) return;

    // If persisted in database with an ID, call direct DELETE API immediately
    if (item && item.id) {
      try {
        const res = await fetch(`${ABOUT_API}/${sectionName}/${item.id}`, {
          method: "DELETE",
        });
        const data = await res.json();
        if (data.status === "success") {
          setter((prev) => prev.filter((_, i) => i !== index));
          showPopup(`${typeLabel} permanently deleted from database`);
          return;
        } else {
          showPopup(data.message || "Failed to delete from database", "error");
          return;
        }
      } catch (err) {
        console.error("Delete error:", err);
        showPopup("Server error while deleting", "error");
        return;
      }
    }

    // If it was just added in the UI without saving yet
    setter((prev) => prev.filter((_, i) => i !== index));
    showPopup(`${typeLabel} removed`);
  };

  // ================= SAVE =================
  const handleSave = async () => {
    setSaving(true);

    try {
      const vaithikasriToSave = vaithikasriList.map((v) => {
        const descParts = [];
        if (v.intro_lead) descParts.push(v.intro_lead.trim());
        descParts.push("The Salient features of our issues are:");
        (v.features || []).forEach((f) => {
          if (f.title && f.title.trim()) {
            descParts.push(`${f.title.trim()}: ${f.detail ? f.detail.trim() : ""}`);
          }
        });
        if (v.closing_note) descParts.push(v.closing_note.trim());

        return {
          id: v.id,
          description: descParts.join("\n"),
          tamil_footer: v.tamil_footer || "",
          image1: v.image1 || "",
          image2: v.image2 || "",
          image1_caption: v.image1_caption || "Sri Kanchi Kamakoti Sabha & Blessing",
          image2_caption: v.image2_caption || "Varshika Veda Poorti Pariksha",
        };
      });

      const res = await fetch(ABOUT_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          vaithikasri_section: vaithikasriToSave,
          founder_section: founderList,
          editor_section: editorList,
        }),
      });

      const data = await res.json();

      if (data.status === "success") {
        showPopup("Saved successfully");
      } else {
        showPopup(data.message || "Save failed", "error");
      }
    } catch (err) {
      console.error(err);
      showPopup("Save error", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "100px", color: "#64748b" }}>
        <div className="spinner-border text-primary" role="status" style={{ color: "#5d1d2b" }}></div>
        <p style={{ marginTop: "15px" }}>Loading About parameters...</p>
      </div>
    );
  }

  // ================= TEMPLATES =================
  const vaithikasriTemplate = {
    intro_lead: "VAITHIKASRI is a spiritual monthly magazine founded by Nannilam Brahmasri V. Ragagopala Ganapatigal. The magazine serves as a spiritual guide to all the religious minded theists.",
    features: [
      { title: "Monthly almanac", detail: "highlighting daily Tithis" },
      { title: "Festivals", detail: "Details of festivals and Viratam" },
      { title: "Doubts Dispelled", detail: "Clarifications from scholars" },
      { title: "Wonderful Stotras", detail: "Effective stotras" },
      { title: "Ethical Sayings", detail: "Spiritual sayings" },
      { title: "Bala Sanskrit", detail: "Sanskrit lessons" },
      { title: "Spiritual News", detail: "Religious events" },
      { title: "Matrimony", detail: "Alliances for brides & grooms" },
      { title: "Ethical Science", detail: "Moral guidance" },
    ],
    closing_note: "Several other interesting features faithfully adhering to Veda Sastras and religious literature.",
    tamil_footer: "",
    image1: "",
    image2: "",
    image1_caption: "Sri Kanchi Kamakoti Sabha & Blessing",
    image2_caption: "Varshika Veda Poorti Pariksha",
  };

  const founderTemplate = {
    name: "",
    image: "",
    summary: "",
    description: "",
    role_badge: "FOUNDER & NITYA AGNIHOTRI",
    tag1: "Sengalipuram Deekshithar",
    tag2: "Vaithikasri Magazine",
    kicker: "SACRED LIFE & CONTRIBUTIONS",
    heading: "Preserving Eternal Vedic Traditions & Dharma Sastras",
    quote: "",
    pillar1_title: "Sacred Lineage",
    pillar1_desc: "",
    pillar2_title: "Dharma Sastra Authority",
    pillar2_desc: "",
    pillar3_title: "Puranic Pravachanam",
    pillar3_desc: "",
    pillar4_title: "Spiritual Legacy",
    pillar4_desc: "",
  };

  const editorTemplate = {
    name: "",
    image: "",
    summary: "",
    description: "",
    role_badge: "CHIEF EDITOR & ACHARYA",
    tag1: "Editor - Vaithikasri",
    tag2: "Sri Seetharama Gurukulam",
    kicker: "CURRENT LEADERSHIP & GURUKULAM ACHARYA",
    heading: "Carrying Forward the Sacred Flame of Vaithikasri",
    quote: "",
    pillar1_title: "Chief Editor",
    pillar1_desc: "",
    pillar2_title: "Gurukulam Acharya",
    pillar2_desc: "",
    pillar3_title: "",
    pillar3_desc: "",
    pillar4_title: "",
    pillar4_desc: "",
  };

  const renderVaithikasri = () => {
    return vaithikasriList.map((item, index) => {
      const cardKey = `history_narrative_${index}`;
      const isEditing = editingKey === cardKey;

      return (
        <div key={index} id={`entry-card-${cardKey}`} className={`admin-entry-card ${isEditing ? "is-editing" : ""}`}>
          
          {/* ENTRY ACTIONS (TOP RIGHT) */}
          <div className="card-actions-top">
            <button
              type="button"
              onClick={() => handleEditEntry("History Narrative", index)}
              className="action-edit"
              title="Edit Vaithikasri Entry"
            >
              <i className="bi bi-pencil"></i>
            </button>
            <button
              type="button"
              onClick={() => handleDeleteEntry("vaithikasri_section", setVaithikasriList, index, item, "Narrative")}
              className="action-delete"
              title="Delete Vaithikasri Entry"
            >
              <i className="bi bi-trash"></i>
            </button>
          </div>

          <h4 className="about-section-subtitle" style={{ color: "#631d29", fontWeight: "800", display: "flex", alignItems: "center", gap: "8px" }}>
            <i className="bi bi-journal-bookmark-fill"></i> Vaithikasri Magazine & Sacred Sabha Controls
          </h4>

          {/* BLOCK 1: MISSION & INTRO LEAD */}
          <div style={{ background: "#ffffff", border: "1.5px solid #e2e8f0", borderRadius: "14px", padding: "20px", marginBottom: "24px", boxShadow: "0 2px 8px rgba(0,0,0,0.03)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "14px", borderBottom: "1px solid #edf2f7", paddingBottom: "10px" }}>
              <span style={{ background: "#631d29", color: "#fff", padding: "3px 10px", borderRadius: "6px", fontSize: "12px", fontWeight: "800" }}>
                SECTION 1
              </span>
              <h5 style={{ margin: 0, fontSize: "15px", fontWeight: "750", color: "#1e293b" }}>
                Magazine Introduction & Mission Statement
              </h5>
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: "13px", fontWeight: "600", color: "#475569", marginBottom: "6px", display: "block" }}>
                Intro Lead Paragraph
              </label>
              <textarea
                value={item.intro_lead || ""}
                onChange={(e) => {
                  const copy = [...vaithikasriList];
                  copy[index].intro_lead = e.target.value;
                  setVaithikasriList(copy);
                }}
                placeholder="e.g. VAITHIKASRI is a spiritual monthly magazine founded by Nannilam Brahmasri V. Ragagopala Ganapatigal. The magazine serves as a spiritual guide to all the religious minded theists."
                style={{ width: "100%", minHeight: "80px", borderRadius: "8px", border: "1.5px solid #cbd5e1", padding: "10px 14px", fontSize: "14px", lineHeight: "1.6" }}
              />
              <small style={{ color: "#64748b", fontSize: "12px", display: "block", marginTop: "4px" }}>
                Displays prominently inside the royal maroon header card on the About page.
              </small>
            </div>
          </div>

          {/* BLOCK 2: SALIENT FEATURES OF ISSUES (STRUCTURED DYNAMIC CARDS) */}
          <div style={{ background: "#ffffff", border: "1.5px solid #e2e8f0", borderRadius: "14px", padding: "20px", marginBottom: "24px", boxShadow: "0 2px 8px rgba(0,0,0,0.03)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", borderBottom: "1px solid #edf2f7", paddingBottom: "10px", flexWrap: "wrap", gap: "10px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span style={{ background: "#631d29", color: "#fff", padding: "3px 10px", borderRadius: "6px", fontSize: "12px", fontWeight: "800" }}>
                  SECTION 2
                </span>
                <div>
                  <h5 style={{ margin: 0, fontSize: "15px", fontWeight: "750", color: "#1e293b" }}>
                    Salient Features of Issues ({item.features ? item.features.length : 0} items)
                  </h5>
                  <small style={{ color: "#64748b" }}>Each item renders as an icon card in the 3-column features grid</small>
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  const copy = [...vaithikasriList];
                  if (!copy[index].features) copy[index].features = [];
                  copy[index].features.push({ title: "", detail: "" });
                  setVaithikasriList(copy);
                }}
                className="btn-add-point"
                style={{ background: "#631d29", color: "#fff", border: "none", padding: "8px 16px", borderRadius: "8px", fontSize: "13px", fontWeight: "600", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px" }}
              >
                <i className="bi bi-plus-circle"></i> Add New Feature
              </button>
            </div>

            {/* FEATURE CARDS LIST */}
            <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "18px" }}>
              {(item.features || []).map((feat, fIdx) => (
                <div
                  key={fIdx}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "36px 1.2fr 2fr 36px",
                    gap: "10px",
                    alignItems: "center",
                    background: "#f8fafc",
                    padding: "10px 14px",
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0"
                  }}
                >
                  <span style={{ background: "#631d29", color: "#fff", borderRadius: "6px", width: "28px", height: "28px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: "700" }}>
                    {fIdx + 1}
                  </span>

                  <div>
                    <input
                      type="text"
                      value={feat.title}
                      onChange={(e) => {
                        const copy = [...vaithikasriList];
                        copy[index].features[fIdx].title = e.target.value;
                        setVaithikasriList(copy);
                      }}
                      placeholder="Feature Title (e.g. Monthly almanac)"
                      style={{ width: "100%", padding: "8px 12px", fontSize: "13px", borderRadius: "6px", border: "1px solid #cbd5e1", margin: 0, fontWeight: "600" }}
                    />
                  </div>

                  <div>
                    <input
                      type="text"
                      value={feat.detail}
                      onChange={(e) => {
                        const copy = [...vaithikasriList];
                        copy[index].features[fIdx].detail = e.target.value;
                        setVaithikasriList(copy);
                      }}
                      placeholder="Detail / Description (e.g. highlighting daily Tithis)"
                      style={{ width: "100%", padding: "8px 12px", fontSize: "13px", borderRadius: "6px", border: "1px solid #cbd5e1", margin: 0 }}
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      const copy = [...vaithikasriList];
                      copy[index].features.splice(fIdx, 1);
                      setVaithikasriList(copy);
                    }}
                    className="action-delete"
                    title="Remove this feature"
                    style={{ width: "32px", height: "32px", border: "none", background: "rgba(239, 68, 68, 0.1)", color: "#ef4444", borderRadius: "6px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                </div>
              ))}
            </div>

            {/* CLOSING GUIDANCE NOTE */}
            <div className="form-group" style={{ marginBottom: 0, borderTop: "1px dashed #cbd5e1", paddingTop: "14px" }}>
              <label style={{ fontSize: "13px", fontWeight: "600", color: "#475569", marginBottom: "6px", display: "block" }}>
                Closing Vedic Guidance Note (Displayed at bottom of features card)
              </label>
              <input
                type="text"
                value={item.closing_note || ""}
                onChange={(e) => {
                  const copy = [...vaithikasriList];
                  copy[index].closing_note = e.target.value;
                  setVaithikasriList(copy);
                }}
                placeholder="e.g. Several other interesting features faithfully adhering to Veda Sastras and religious literature."
                style={{ width: "100%", padding: "8px 12px", fontSize: "13px", borderRadius: "8px", border: "1.5px solid #cbd5e1" }}
              />
            </div>
          </div>

          {/* BLOCK 3: SACRED PHOTOS & DYNAMIC CAPTIONS */}
          <div style={{ background: "#ffffff", border: "1.5px solid #e2e8f0", borderRadius: "14px", padding: "20px", marginBottom: "24px", boxShadow: "0 2px 8px rgba(0,0,0,0.03)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "14px", borderBottom: "1px solid #edf2f7", paddingBottom: "10px" }}>
              <span style={{ background: "#631d29", color: "#fff", padding: "3px 10px", borderRadius: "6px", fontSize: "12px", fontWeight: "800" }}>
                SECTION 3
              </span>
              <h5 style={{ margin: 0, fontSize: "15px", fontWeight: "750", color: "#1e293b" }}>
                Sacred Sabha & Blessing Photos (Left Gallery Showcase)
              </h5>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
              {[
                { field: "image1", captionField: "image1_caption", label: "Photo 1 (Top Gallery Card)", defaultCaption: "Sri Kanchi Kamakoti Sabha & Blessing" },
                { field: "image2", captionField: "image2_caption", label: "Photo 2 (Bottom Gallery Card)", defaultCaption: "Varshika Veda Poorti Pariksha" },
              ].map(({ field, captionField, label, defaultCaption }) => (
                <div key={field} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "16px" }}>
                  <span style={{ display: "block", fontSize: "12px", fontWeight: "700", marginBottom: "10px", textTransform: "uppercase", color: "#64748b" }}>
                    {label}
                  </span>

                  {item[field] ? (
                    <div style={{ position: "relative", marginBottom: "12px", borderRadius: "8px", overflow: "hidden", border: "1px solid #cbd5e1", background: "#fff" }}>
                      <img
                        src={API_BASE + item[field]}
                        alt={field}
                        style={{ width: "100%", height: "160px", objectFit: "cover", display: "block" }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const copy = [...vaithikasriList];
                          copy[index][field] = "";
                          setVaithikasriList(copy);
                        }}
                        className="action-delete"
                        style={{ position: "absolute", top: "6px", right: "6px", width: "28px", height: "28px", background: "rgba(0,0,0,0.6)", color: "#fff", borderRadius: "50%", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>
                    </div>
                  ) : (
                    <div className="about-upload-zone" style={{ height: "130px", marginBottom: "12px" }}>
                      <i className="bi bi-cloud-arrow-up"></i>
                      <p>Upload Photo</p>
                      <input
                        type="file"
                        onChange={(e) =>
                          handleImageUpload(e.target.files[0], (path) => {
                            const copy = [...vaithikasriList];
                            copy[index][field] = path;
                            setVaithikasriList(copy);
                          })
                        }
                        accept="image/*"
                      />
                    </div>
                  )}

                  <div>
                    <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569", marginBottom: "4px", display: "block" }}>
                      Photo Caption / Label
                    </label>
                    <input
                      type="text"
                      value={item[captionField] !== undefined ? item[captionField] : defaultCaption}
                      onChange={(e) => {
                        const copy = [...vaithikasriList];
                        copy[index][captionField] = e.target.value;
                        setVaithikasriList(copy);
                      }}
                      placeholder={`e.g. ${defaultCaption}`}
                      style={{ width: "100%", padding: "8px 12px", fontSize: "13px", borderRadius: "6px", border: "1px solid #cbd5e1" }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* BLOCK 4: TAMIL FOOTER MESSAGE */}
          <div style={{ background: "#ffffff", border: "1.5px solid #e2e8f0", borderRadius: "14px", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.03)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "14px", borderBottom: "1px solid #edf2f7", paddingBottom: "10px" }}>
              <span style={{ background: "#631d29", color: "#fff", padding: "3px 10px", borderRadius: "6px", fontSize: "12px", fontWeight: "800" }}>
                SECTION 4
              </span>
              <h5 style={{ margin: 0, fontSize: "15px", fontWeight: "750", color: "#1e293b" }}>
                Tamil Footer Banner Text
              </h5>
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <textarea
                value={item.tamil_footer || ""}
                onChange={(e) => {
                  const copy = [...vaithikasriList];
                  copy[index].tamil_footer = e.target.value;
                  setVaithikasriList(copy);
                }}
                placeholder="அந்தந்த மாதத்தின் விரதங்கள் பண்டிகைகள் விபரம்..."
                style={{ width: "100%", minHeight: "90px", borderRadius: "8px", border: "1.5px solid #cbd5e1", padding: "10px 14px", fontSize: "14px", lineHeight: "1.8" }}
              />
              <small style={{ color: "#64748b", fontSize: "12px", display: "block", marginTop: "4px" }}>
                Displays in the royal maroon banner strip spanning the full width beneath the section.
              </small>
            </div>
          </div>

        </div>
      );
    });
  };

  const renderFounders = () => {
    return founderList.map((item, index) => {
      const cardKey = `founder_profile_${index}`;
      const isEditing = editingKey === cardKey;

      return (
        <div key={index} id={`entry-card-${cardKey}`} className={`admin-entry-card ${isEditing ? "is-editing" : ""}`}>
          
          {/* ENTRY ACTIONS (TOP RIGHT) */}
          <div className="card-actions-top">
            <button
              type="button"
              onClick={() => handleEditEntry("Founder Profile", index, item.name)}
              className="action-edit"
              title="Edit Founder Profile"
            >
              <i className="bi bi-pencil"></i>
            </button>
          </div>

          <h4 className="about-section-subtitle">
            <i className="bi bi-person-circle me-2"></i> Founder Identity & Profile Card
          </h4>

          <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: "24px", alignItems: "start", marginBottom: "24px" }}>
            
            {/* PROFILE IMAGE */}
            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "16px", textAlign: "center", width: "220px" }}>
              <span style={{ display: "block", fontSize: "11.5px", fontWeight: "700", marginBottom: "10px", color: "#64748b", textTransform: "uppercase" }}>
                Founder Image / Portrait
              </span>
              {item.image ? (
                <div style={{ position: "relative" }}>
                  <img
                    src={API_BASE + item.image}
                    alt="Founder"
                    style={{ width: "100%", height: "auto", maxHeight: "190px", objectFit: "contain", borderRadius: "12px", border: "2.5px solid #5d1d2b", background: "#fff" }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const copy = [...founderList];
                      copy[index].image = "";
                      setFounderList(copy);
                    }}
                    className="action-delete"
                    style={{ position: "absolute", top: "4px", right: "4px", width: "26px", height: "26px" }}
                  >
                    <i className="bi bi-x"></i>
                  </button>
                </div>
              ) : (
                <div className="about-upload-zone" style={{ height: "150px" }}>
                  <i className="bi bi-cloud-arrow-up"></i>
                  <p>Upload Photo</p>
                  <input
                    type="file"
                    onChange={(e) =>
                      handleImageUpload(e.target.files[0], (path) => {
                        const copy = [...founderList];
                        copy[index].image = path;
                        setFounderList(copy);
                      })
                    }
                    accept="image/*"
                  />
                </div>
              )}
            </div>

            {/* IDENTITY DETAILS */}
            <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "16px" }}>
              <div className="about-grid-columns" style={{ gridTemplateColumns: "1.2fr 1fr" }}>
                <div className="form-group">
                  <label>Founder Name</label>
                  <input
                    type="text"
                    value={item.name || ""}
                    onChange={(e) => {
                      const copy = [...founderList];
                      copy[index].name = e.target.value;
                      setFounderList(copy);
                    }}
                    placeholder="e.g. BRAHMASRI V RAJAGOPAL GANAPADIGAL"
                  />
                </div>
                <div className="form-group">
                  <label>Role Badge / Title</label>
                  <input
                    type="text"
                    value={item.role_badge || ""}
                    onChange={(e) => {
                      const copy = [...founderList];
                      copy[index].role_badge = e.target.value;
                      setFounderList(copy);
                    }}
                    placeholder="e.g. FOUNDER & NITYA AGNIHOTRI"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Intro Summary (Left Card)</label>
                <textarea
                  value={item.summary || ""}
                  onChange={(e) => {
                    const copy = [...founderList];
                    copy[index].summary = e.target.value;
                    setFounderList(copy);
                  }}
                  placeholder="Summary text below name on left card..."
                  style={{ minHeight: "65px" }}
                />
              </div>

              <div className="about-grid-columns" style={{ gridTemplateColumns: "1fr 1fr" }}>
                <div className="form-group">
                  <label>Badge Seal Tag #1</label>
                  <input
                    type="text"
                    value={item.tag1 || ""}
                    onChange={(e) => {
                      const copy = [...founderList];
                      copy[index].tag1 = e.target.value;
                      setFounderList(copy);
                    }}
                    placeholder="e.g. Sengalipuram Deekshithar"
                  />
                </div>
                <div className="form-group">
                  <label>Badge Seal Tag #2</label>
                  <input
                    type="text"
                    value={item.tag2 || ""}
                    onChange={(e) => {
                      const copy = [...founderList];
                      copy[index].tag2 = e.target.value;
                      setFounderList(copy);
                    }}
                    placeholder="e.g. Vaithikasri Magazine"
                  />
                </div>
              </div>
            </div>
          </div>

          <hr style={{ border: "none", borderTop: "1px dashed #cbd5e1", margin: "24px 0" }} />

          {/* NARRATIVE CARD DETAILS */}
          <h4 className="about-section-subtitle">
            <i className="bi bi-journal-richtext me-2"></i> Right Narrative & Biographical Life
          </h4>

          <div className="about-grid-columns" style={{ gridTemplateColumns: "1fr 1.5fr", marginBottom: "16px" }}>
            <div className="form-group">
              <label>Kicker / Section Pill</label>
              <input
                type="text"
                value={item.kicker || ""}
                onChange={(e) => {
                  const copy = [...founderList];
                  copy[index].kicker = e.target.value;
                  setFounderList(copy);
                }}
                placeholder="e.g. SACRED LIFE & CONTRIBUTIONS"
              />
            </div>
            <div className="form-group">
              <label>Narrative Main Heading</label>
              <input
                type="text"
                value={item.heading || ""}
                onChange={(e) => {
                  const copy = [...founderList];
                  copy[index].heading = e.target.value;
                  setFounderList(copy);
                }}
                placeholder="e.g. Preserving Eternal Vedic Traditions & Dharma Sastras"
              />
            </div>
          </div>

          <div className="form-group" style={{ marginBottom: "16px" }}>
            <label>Highlight Quote Box</label>
            <textarea
              value={item.quote || ""}
              onChange={(e) => {
                const copy = [...founderList];
                copy[index].quote = e.target.value;
                setFounderList(copy);
              }}
              placeholder="Inspirational quotation displayed in the golden quote box..."
              style={{ minHeight: "65px" }}
            />
          </div>

          <div className="form-group" style={{ marginBottom: "24px" }}>
            <label>Detailed Biography Paragraphs</label>
            <textarea
              value={item.description || ""}
              onChange={(e) => {
                const copy = [...founderList];
                copy[index].description = e.target.value;
                setFounderList(copy);
              }}
              placeholder="Enter biographical narrative (press Enter for separate paragraphs)..."
              style={{ minHeight: "130px" }}
            />
          </div>

          <hr style={{ border: "none", borderTop: "1px dashed #cbd5e1", margin: "24px 0" }} />

          {/* 4 CORE PILLARS */}
          <h4 className="about-section-subtitle">
            <i className="bi bi-grid-fill me-2"></i> 4 Core Legacy Pillars (Bottom Highlights)
          </h4>

          <div className="about-grid-columns" style={{ gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
            
            {/* Pillar 1 */}
            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "14px" }}>
              <span style={{ fontSize: "11px", fontWeight: "800", color: "#5d1d2b", textTransform: "uppercase" }}>Pillar 1</span>
              <div className="form-group" style={{ marginTop: "6px" }}>
                <label>Title</label>
                <input
                  type="text"
                  value={item.pillar1_title || ""}
                  onChange={(e) => {
                    const copy = [...founderList];
                    copy[index].pillar1_title = e.target.value;
                    setFounderList(copy);
                  }}
                  placeholder="e.g. Sacred Lineage"
                />
              </div>
              <div className="form-group" style={{ marginTop: "6px" }}>
                <label>Description</label>
                <textarea
                  value={item.pillar1_desc || ""}
                  onChange={(e) => {
                    const copy = [...founderList];
                    copy[index].pillar1_desc = e.target.value;
                    setFounderList(copy);
                  }}
                  placeholder="Pillar description..."
                  style={{ minHeight: "45px" }}
                />
              </div>
            </div>

            {/* Pillar 2 */}
            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "14px" }}>
              <span style={{ fontSize: "11px", fontWeight: "800", color: "#5d1d2b", textTransform: "uppercase" }}>Pillar 2</span>
              <div className="form-group" style={{ marginTop: "6px" }}>
                <label>Title</label>
                <input
                  type="text"
                  value={item.pillar2_title || ""}
                  onChange={(e) => {
                    const copy = [...founderList];
                    copy[index].pillar2_title = e.target.value;
                    setFounderList(copy);
                  }}
                  placeholder="e.g. Dharma Sastra Authority"
                />
              </div>
              <div className="form-group" style={{ marginTop: "6px" }}>
                <label>Description</label>
                <textarea
                  value={item.pillar2_desc || ""}
                  onChange={(e) => {
                    const copy = [...founderList];
                    copy[index].pillar2_desc = e.target.value;
                    setFounderList(copy);
                  }}
                  placeholder="Pillar description..."
                  style={{ minHeight: "45px" }}
                />
              </div>
            </div>

            {/* Pillar 3 */}
            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "14px" }}>
              <span style={{ fontSize: "11px", fontWeight: "800", color: "#5d1d2b", textTransform: "uppercase" }}>Pillar 3</span>
              <div className="form-group" style={{ marginTop: "6px" }}>
                <label>Title</label>
                <input
                  type="text"
                  value={item.pillar3_title || ""}
                  onChange={(e) => {
                    const copy = [...founderList];
                    copy[index].pillar3_title = e.target.value;
                    setFounderList(copy);
                  }}
                  placeholder="e.g. Puranic Pravachanam"
                />
              </div>
              <div className="form-group" style={{ marginTop: "6px" }}>
                <label>Description</label>
                <textarea
                  value={item.pillar3_desc || ""}
                  onChange={(e) => {
                    const copy = [...founderList];
                    copy[index].pillar3_desc = e.target.value;
                    setFounderList(copy);
                  }}
                  placeholder="Pillar description..."
                  style={{ minHeight: "45px" }}
                />
              </div>
            </div>

            {/* Pillar 4 */}
            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "14px" }}>
              <span style={{ fontSize: "11px", fontWeight: "800", color: "#5d1d2b", textTransform: "uppercase" }}>Pillar 4</span>
              <div className="form-group" style={{ marginTop: "6px" }}>
                <label>Title</label>
                <input
                  type="text"
                  value={item.pillar4_title || ""}
                  onChange={(e) => {
                    const copy = [...founderList];
                    copy[index].pillar4_title = e.target.value;
                    setFounderList(copy);
                  }}
                  placeholder="e.g. Spiritual Legacy"
                />
              </div>
              <div className="form-group" style={{ marginTop: "6px" }}>
                <label>Description</label>
                <textarea
                  value={item.pillar4_desc || ""}
                  onChange={(e) => {
                    const copy = [...founderList];
                    copy[index].pillar4_desc = e.target.value;
                    setFounderList(copy);
                  }}
                  placeholder="Pillar description..."
                  style={{ minHeight: "45px" }}
                />
              </div>
            </div>

          </div>

        </div>
      );
    });
  };

  const renderEditors = () => {
    return editorList.map((item, index) => {
      const cardKey = `editor_profile_${index}`;
      const isEditing = editingKey === cardKey;

      return (
        <div key={index} id={`entry-card-${cardKey}`} className={`admin-entry-card ${isEditing ? "is-editing" : ""}`}>
          
          {/* ENTRY ACTIONS (TOP RIGHT) */}
          <div className="card-actions-top">
            <button
              type="button"
              onClick={() => handleEditEntry("Editor Profile", index, item.name)}
              className="action-edit"
              title="Edit Editor Profile"
            >
              <i className="bi bi-pencil"></i>
            </button>
          </div>

          <h4 className="about-section-subtitle">
            <i className="bi bi-person-badge me-2"></i> Chief Editor Identity & Profile Card
          </h4>

          <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: "24px", alignItems: "start", marginBottom: "24px" }}>
            
            {/* PROFILE IMAGE */}
            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "16px", textAlign: "center", width: "220px" }}>
              <span style={{ display: "block", fontSize: "11.5px", fontWeight: "700", marginBottom: "10px", color: "#64748b", textTransform: "uppercase" }}>
                Editor Image / Portrait
              </span>
              {item.image ? (
                <div style={{ position: "relative" }}>
                  <img
                    src={API_BASE + item.image}
                    alt="Editor"
                    style={{ width: "100%", height: "auto", maxHeight: "190px", objectFit: "contain", borderRadius: "12px", border: "2.5px solid #5d1d2b", background: "#fff" }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const copy = [...editorList];
                      copy[index].image = "";
                      setEditorList(copy);
                    }}
                    className="action-delete"
                    style={{ position: "absolute", top: "4px", right: "4px", width: "26px", height: "26px" }}
                  >
                    <i className="bi bi-x"></i>
                  </button>
                </div>
              ) : (
                <div className="about-upload-zone" style={{ height: "150px" }}>
                  <i className="bi bi-cloud-arrow-up"></i>
                  <p>Upload Photo</p>
                  <input
                    type="file"
                    onChange={(e) =>
                      handleImageUpload(e.target.files[0], (path) => {
                        const copy = [...editorList];
                        copy[index].image = path;
                        setEditorList(copy);
                      })
                    }
                    accept="image/*"
                  />
                </div>
              )}
            </div>

            {/* IDENTITY DETAILS */}
            <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "16px" }}>
              <div className="about-grid-columns" style={{ gridTemplateColumns: "1.2fr 1fr" }}>
                <div className="form-group">
                  <label>Editor Name</label>
                  <input
                    type="text"
                    value={item.name || ""}
                    onChange={(e) => {
                      const copy = [...editorList];
                      copy[index].name = e.target.value;
                      setEditorList(copy);
                    }}
                    placeholder="e.g. NANNILAM BRAHMASRI R SRIKRISHNA GANAPATIGAL"
                  />
                </div>
                <div className="form-group">
                  <label>Role Badge / Title</label>
                  <input
                    type="text"
                    value={item.role_badge || ""}
                    onChange={(e) => {
                      const copy = [...editorList];
                      copy[index].role_badge = e.target.value;
                      setEditorList(copy);
                    }}
                    placeholder="e.g. CHIEF EDITOR & ACHARYA"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Intro Summary / Credentials</label>
                <textarea
                  value={item.summary || ""}
                  onChange={(e) => {
                    const copy = [...editorList];
                    copy[index].summary = e.target.value;
                    setEditorList(copy);
                  }}
                  placeholder="Summary text below name on card..."
                  style={{ minHeight: "65px" }}
                />
              </div>

              <div className="about-grid-columns" style={{ gridTemplateColumns: "1fr 1fr" }}>
                <div className="form-group">
                  <label>Badge Seal Tag #1</label>
                  <input
                    type="text"
                    value={item.tag1 || ""}
                    onChange={(e) => {
                      const copy = [...editorList];
                      copy[index].tag1 = e.target.value;
                      setEditorList(copy);
                    }}
                    placeholder="e.g. Editor - Vaithikasri"
                  />
                </div>
                <div className="form-group">
                  <label>Badge Seal Tag #2</label>
                  <input
                    type="text"
                    value={item.tag2 || ""}
                    onChange={(e) => {
                      const copy = [...editorList];
                      copy[index].tag2 = e.target.value;
                      setEditorList(copy);
                    }}
                    placeholder="e.g. Sri Seetharama Gurukulam"
                  />
                </div>
              </div>
            </div>
          </div>

          <hr style={{ border: "none", borderTop: "1px dashed #cbd5e1", margin: "24px 0" }} />

          {/* NARRATIVE CARD DETAILS */}
          <h4 className="about-section-subtitle">
            <i className="bi bi-journal-richtext me-2"></i> Editorial Leadership & Gurukulam Narrative
          </h4>

          <div className="about-grid-columns" style={{ gridTemplateColumns: "1fr 1.5fr", marginBottom: "16px" }}>
            <div className="form-group">
              <label>Kicker / Section Pill</label>
              <input
                type="text"
                value={item.kicker || ""}
                onChange={(e) => {
                  const copy = [...editorList];
                  copy[index].kicker = e.target.value;
                  setEditorList(copy);
                }}
                placeholder="e.g. CURRENT LEADERSHIP & GURUKULAM ACHARYA"
              />
            </div>
            <div className="form-group">
              <label>Narrative Main Heading</label>
              <input
                type="text"
                value={item.heading || ""}
                onChange={(e) => {
                  const copy = [...editorList];
                  copy[index].heading = e.target.value;
                  setEditorList(copy);
                }}
                placeholder="e.g. Carrying Forward the Sacred Flame of Vaithikasri"
              />
            </div>
          </div>

          <div className="form-group" style={{ marginBottom: "16px" }}>
            <label>Highlight Quote Box</label>
            <textarea
              value={item.quote || ""}
              onChange={(e) => {
                const copy = [...editorList];
                copy[index].quote = e.target.value;
                setEditorList(copy);
              }}
              placeholder="Inspirational quotation displayed in the golden quote box..."
              style={{ minHeight: "65px" }}
            />
          </div>

          <div className="form-group" style={{ marginBottom: "24px" }}>
            <label>Detailed Editorial Biography Paragraphs</label>
            <textarea
              value={item.description || ""}
              onChange={(e) => {
                const copy = [...editorList];
                copy[index].description = e.target.value;
                setEditorList(copy);
              }}
              placeholder="Enter editorial narrative (press Enter for separate paragraphs)..."
              style={{ minHeight: "130px" }}
            />
          </div>

          <hr style={{ border: "none", borderTop: "1px dashed #cbd5e1", margin: "24px 0" }} />

          {/* LEADERSHIP PILLARS */}
          <h4 className="about-section-subtitle">
            <i className="bi bi-grid-fill me-2"></i> Key Roles & Leadership Pillars (Highlights)
          </h4>

          <div className="about-grid-columns" style={{ gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
            
            {/* Pillar 1 */}
            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "14px" }}>
              <span style={{ fontSize: "11px", fontWeight: "800", color: "#5d1d2b", textTransform: "uppercase" }}>Pillar 1</span>
              <div className="form-group" style={{ marginTop: "6px" }}>
                <label>Title</label>
                <input
                  type="text"
                  value={item.pillar1_title || ""}
                  onChange={(e) => {
                    const copy = [...editorList];
                    copy[index].pillar1_title = e.target.value;
                    setEditorList(copy);
                  }}
                  placeholder="e.g. Chief Editor"
                />
              </div>
              <div className="form-group" style={{ marginTop: "6px" }}>
                <label>Description</label>
                <textarea
                  value={item.pillar1_desc || ""}
                  onChange={(e) => {
                    const copy = [...editorList];
                    copy[index].pillar1_desc = e.target.value;
                    setEditorList(copy);
                  }}
                  placeholder="Pillar description..."
                  style={{ minHeight: "45px" }}
                />
              </div>
            </div>

            {/* Pillar 2 */}
            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "14px" }}>
              <span style={{ fontSize: "11px", fontWeight: "800", color: "#5d1d2b", textTransform: "uppercase" }}>Pillar 2</span>
              <div className="form-group" style={{ marginTop: "6px" }}>
                <label>Title</label>
                <input
                  type="text"
                  value={item.pillar2_title || ""}
                  onChange={(e) => {
                    const copy = [...editorList];
                    copy[index].pillar2_title = e.target.value;
                    setEditorList(copy);
                  }}
                  placeholder="e.g. Gurukulam Acharya"
                />
              </div>
              <div className="form-group" style={{ marginTop: "6px" }}>
                <label>Description</label>
                <textarea
                  value={item.pillar2_desc || ""}
                  onChange={(e) => {
                    const copy = [...editorList];
                    copy[index].pillar2_desc = e.target.value;
                    setEditorList(copy);
                  }}
                  placeholder="Pillar description..."
                  style={{ minHeight: "45px" }}
                />
              </div>
            </div>

            {/* Pillar 3 */}
            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "14px" }}>
              <span style={{ fontSize: "11px", fontWeight: "800", color: "#5d1d2b", textTransform: "uppercase" }}>Pillar 3 (Optional)</span>
              <div className="form-group" style={{ marginTop: "6px" }}>
                <label>Title</label>
                <input
                  type="text"
                  value={item.pillar3_title || ""}
                  onChange={(e) => {
                    const copy = [...editorList];
                    copy[index].pillar3_title = e.target.value;
                    setEditorList(copy);
                  }}
                  placeholder="Optional Pillar Title"
                />
              </div>
              <div className="form-group" style={{ marginTop: "6px" }}>
                <label>Description</label>
                <textarea
                  value={item.pillar3_desc || ""}
                  onChange={(e) => {
                    const copy = [...editorList];
                    copy[index].pillar3_desc = e.target.value;
                    setEditorList(copy);
                  }}
                  placeholder="Optional description..."
                  style={{ minHeight: "45px" }}
                />
              </div>
            </div>

            {/* Pillar 4 */}
            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "14px" }}>
              <span style={{ fontSize: "11px", fontWeight: "800", color: "#5d1d2b", textTransform: "uppercase" }}>Pillar 4 (Optional)</span>
              <div className="form-group" style={{ marginTop: "6px" }}>
                <label>Title</label>
                <input
                  type="text"
                  value={item.pillar4_title || ""}
                  onChange={(e) => {
                    const copy = [...editorList];
                    copy[index].pillar4_title = e.target.value;
                    setEditorList(copy);
                  }}
                  placeholder="Optional Pillar Title"
                />
              </div>
              <div className="form-group" style={{ marginTop: "6px" }}>
                <label>Description</label>
                <textarea
                  value={item.pillar4_desc || ""}
                  onChange={(e) => {
                    const copy = [...editorList];
                    copy[index].pillar4_desc = e.target.value;
                    setEditorList(copy);
                  }}
                  placeholder="Optional description..."
                  style={{ minHeight: "45px" }}
                />
              </div>
            </div>

          </div>

        </div>
      );
    });
  };

  return (
    <div className="admin-container about-admin-container">
      
      {/* ================= HEADER ================= */}
      <div className="about-header">
        <h2>
          <i className="bi bi-info-circle-fill" style={{ color: "#5d1d2b", marginRight: "10px" }}></i> About Page Customization
        </h2>
        <p>Configure company achievements, historic narrative, founders, and expert editorial panel</p>
      </div>

      {/* ================= IN-PAGE NAVIGATION NAVBAR ================= */}
      <div className="in-page-navbar">
        <button
          type="button"
          className={`in-page-nav-link ${activeTab === "narrative" ? "active" : ""}`}
          onClick={() => setActiveTab("narrative")}
        >
          <i className="bi bi-journal-text"></i> Vaithikasri
        </button>
        <span className="in-page-navbar-divider">|</span>
        <button
          type="button"
          className={`in-page-nav-link ${activeTab === "founders" ? "active" : ""}`}
          onClick={() => setActiveTab("founders")}
        >
          <i className="bi bi-people"></i> The Founder
        </button>
        <span className="in-page-navbar-divider">|</span>
        <button
          type="button"
          className={`in-page-nav-link ${activeTab === "editors" ? "active" : ""}`}
          onClick={() => setActiveTab("editors")}
        >
          <i className="bi bi-person-badge"></i> The Editor
        </button>
      </div>

      {/* ================= STATS PANEL ================= */}
      <div className="admin-stats">
        
        <div className="stat-card" onClick={() => setActiveTab("narrative")} style={{ cursor: "pointer" }}>
          <div className="stat-info">
            <h4>Vaithikasri Features</h4>
            <p>{vaithikasriList.reduce((sum, item) => sum + (Array.isArray(item.features) ? item.features.length : 0), 0)} Points</p>
          </div>
          <div className="stat-icon-wrapper">
            <i className="bi bi-chat-left-quote-fill"></i>
          </div>
        </div>

        <div className="stat-card" onClick={() => setActiveTab("founders")} style={{ cursor: "pointer" }}>
          <div className="stat-info">
            <h4>Founder Profile</h4>
            <p>{founderList.length > 0 ? "Configured" : "Not Set"}</p>
          </div>
          <div className="stat-icon-wrapper">
            <i className="bi bi-person-fill-check"></i>
          </div>
        </div>

        <div className="stat-card" onClick={() => setActiveTab("editors")} style={{ cursor: "pointer" }}>
          <div className="stat-info">
            <h4>Editor Profile</h4>
            <p>{editorList.length > 0 ? "Configured" : "Not Set"}</p>
          </div>
          <div className="stat-icon-wrapper">
            <i className="bi bi-shield-check"></i>
          </div>
        </div>

      </div>

      {/* ================= VAITHIKASRI SECTION ================= */}
      {activeTab === "narrative" && (
        <section className="admin-section" id="vaithikasri-section">
          <div className="admin-section-header">
            <h2>
              <i className="bi bi-journal-text"></i> Vaithikasri History & Narrative Section
            </h2>
            <button
              type="button"
              onClick={() => handleAddEntry(setVaithikasriList, vaithikasriTemplate)}
              className="btn-premium"
            >
              <i className="bi bi-plus-circle"></i> Add History block
            </button>
          </div>

          {vaithikasriList.length === 0 ? (
            <div className="admin-empty-state">
              <i className="bi bi-journal"></i>
              <h3>No History Narrative</h3>
              <p>Click "Add History block" to create narrative text content.</p>
            </div>
          ) : (
            renderVaithikasri()
          )}
        </section>
      )}

      {/* ================= FOUNDER SECTION ================= */}
      {activeTab === "founders" && (
        <section className="admin-section" id="founder-section">
          <div className="admin-section-header">
            <div>
              <h2>
                <i className="bi bi-person-fill-gear"></i> Founder Biographical Profile
              </h2>
              <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: "#64748b" }}>
                Manage the founder's biography, credentials, portrait, and Vedic contributions
              </p>
            </div>
            {founderList.length === 0 && (
              <button
                type="button"
                onClick={() => handleAddEntry(setFounderList, founderTemplate)}
                className="btn-premium"
              >
                <i className="bi bi-plus-circle"></i> Initialize Founder Profile
              </button>
            )}
          </div>

          {founderList.length === 0 ? (
            <div className="admin-empty-state">
              <i className="bi bi-person-plus"></i>
              <h3>No Founder Registered</h3>
              <p>Click "Initialize Founder Profile" to set up the founder card.</p>
            </div>
          ) : (
            renderFounders()
          )}
        </section>
      )}

      {/* ================= EDITOR SECTION ================= */}
      {activeTab === "editors" && (
        <section className="admin-section" id="editor-section">
          <div className="admin-section-header">
            <div>
              <h2>
                <i className="bi bi-person-workspace"></i> Editor Profile & Details
              </h2>
              <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: "#64748b" }}>
                Manage the current chief editor's biography, credentials, and Gurukulam leadership
              </p>
            </div>
            {editorList.length === 0 && (
              <button
                type="button"
                onClick={() => handleAddEntry(setEditorList, editorTemplate)}
                className="btn-premium"
              >
                <i className="bi bi-plus-circle"></i> Initialize Editor Profile
              </button>
            )}
          </div>

          {editorList.length === 0 ? (
            <div className="admin-empty-state">
              <i className="bi bi-person-gear"></i>
              <h3>No Editor Listed</h3>
              <p>Click "Initialize Editor Profile" to configure the chief editor details.</p>
            </div>
          ) : (
            renderEditors()
          )}
        </section>
      )}

      {/* ================= SAVE ACTION BAR ================= */}
      <div className="about-action-bar">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="btn-premium"
        >
          {saving ? (
            <><span className="spinner-border spinner-border-sm" role="status" style={{ marginRight: "8px" }}></span>Saving Changes...</>
          ) : (
            <><i className="bi bi-check-circle" style={{ marginRight: "8px" }}></i>Save About Page Configurations</>
          )}
        </button>
      </div>

    </div>
  );
};

export default AdminAbout;
